import { useTerminalStore } from '../store/useTerminalStore';
import type { PositionData, InstrumentInfo } from '../types/gateway';

export interface InstrumentSelectorProps {
  selected: string;
  onSelect: (symbol: string) => void;
}

const FALLBACK_INSTRUMENTS = [
  'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'ONDOUSDT',
  'SUIUSDT', 'DOGEUSDT', 'HYPEUSDT', '1000PEPEUSDT', 'NEARUSDT',
  'ADAUSDT', 'LINKUSDT', 'BNBUSDT',
] as const;

type Instrument = string;

/** Derive the base asset ticker from a USDT-margined symbol.
 *  e.g. "BTCUSDT" → "BTC", "1000PEPEUSDT" → "1000PEPE"
 */
function baseAsset(symbol: string): string {
  if (symbol.endsWith('USDT')) {
    return symbol.slice(0, -4);
  }
  return symbol;
}

function findPosition(positions: PositionData[], symbol: string): PositionData | undefined {
  return positions.find((p) => p.symbol === symbol);
}

function formatPnl(pnl: number): string {
  const sign = pnl >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(pnl).toFixed(2)}`;
}

interface PositionBadgeProps {
  position: PositionData;
  symbol: string;
}

function PositionBadge({ position, symbol }: PositionBadgeProps) {
  const isLong = position.qty > 0;
  const side = isLong ? 'LONG' : 'SHORT';
  const absQty = Math.abs(position.qty);
  const base = baseAsset(symbol);
  const pnl = position.unrealized_pnl;
  const pnlPositive = pnl >= 0;

  return (
    <div className="px-3 pb-1.5 space-y-0.5">
      {/* Side + qty */}
      <div className="flex items-center gap-1">
        <span
          className={[
            'text-[8px] font-mono px-1 rounded-sm',
            isLong
              ? 'bg-success/20 text-success'
              : 'bg-danger/20 text-danger',
          ].join(' ')}
          data-testid={`position-badge-${symbol}`}
        >
          {side}
        </span>
        <span className="text-[8px] font-mono text-muted-foreground">
          {absQty} {base}
        </span>
      </div>
      {/* PnL */}
      <div
        className={[
          'text-[8px] font-mono',
          pnlPositive ? 'text-success' : 'text-danger',
        ].join(' ')}
        data-testid={`pnl-${symbol}`}
      >
        PnL {formatPnl(pnl)}
      </div>
    </div>
  );
}

function formatPrice(info: InstrumentInfo | undefined): string {
  if (!info) return '\u2500';
  const p = info.last_price;
  if (p >= 10000) return '$' + Math.round(p).toLocaleString();
  if (p >= 100) return '$' + p.toFixed(1);
  if (p >= 1) return '$' + p.toFixed(2);
  return '$' + p.toFixed(4);
}

export function InstrumentSelector({ selected, onSelect }: InstrumentSelectorProps) {
  const positions = useTerminalStore((s) => s.positions);
  const instruments = useTerminalStore((s) => s.instruments);
  const storeSymbols = Object.keys(instruments);
  const symbolList: Instrument[] = storeSymbols.length > 0
    ? storeSymbols
    : [...FALLBACK_INSTRUMENTS];
  const isDynamic = storeSymbols.length > 0;

  return (
    <div
      className="w-full shrink-0 border-b border-border bg-[#020305] flex flex-col lg:w-40 lg:border-b-0 lg:border-r"
      role="navigation"
      aria-label="Instrument selector"
      data-testid="instrument-selector"
    >
      {/* Header */}
      <div className="bg-[#05070A] border-b border-border h-8 flex items-center px-3 shrink-0 gap-2">
        <span className="text-[9px] font-mono font-bold tracking-widest text-primary uppercase">
          INSTRUMENTS
        </span>
        <span className="flex items-center gap-1 ml-auto">
          <span className="relative flex h-1.5 w-1.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isDynamic ? 'bg-success' : 'bg-warning'} opacity-75`} />
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isDynamic ? 'bg-success' : 'bg-warning'}`} />
          </span>
          <span className={`text-[7px] font-mono uppercase tracking-widest ${isDynamic ? 'text-success' : 'text-warning'}`}>{isDynamic ? 'LIVE' : 'STATIC'}</span>
        </span>
      </div>

      {/* Scrollable instrument list */}
      <div className="flex overflow-x-auto lg:block lg:flex-1 lg:overflow-x-hidden lg:overflow-y-auto">
        {symbolList.map((instrument: Instrument) => {
          const isActive = selected === instrument;
          const position = findPosition(positions, instrument);

          return (
            <div key={instrument} className="w-36 shrink-0 border-r border-border-subtle last:border-r-0 lg:w-auto lg:border-r-0">
              {/* Instrument row */}
              <button
                onClick={() => onSelect(instrument)}
                className={[
                  'w-full text-left px-3 py-1.5 cursor-pointer border-l-2 transition-colors',
                  'flex items-center justify-between',
                  isActive
                    ? 'border-l-primary bg-primary/5 text-foreground font-bold'
                    : 'border-transparent text-muted-foreground hover:bg-white/5 hover:text-foreground',
                ].join(' ')}
                aria-pressed={isActive}
              >
                <span className="text-[10px] font-mono font-bold truncate">{instrument}</span>
                <span
                  className="text-[9px] font-mono text-muted-foreground ml-1 shrink-0"
                  aria-label={`${instrument} price`}
                >
                  {formatPrice(instruments[instrument])}
                </span>
              </button>

              {/* Position badge (only when a position exists for this symbol) */}
              {position !== undefined && (
                <PositionBadge position={position} symbol={instrument} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
